import React, { useEffect, useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions';
import {
  createContactNote,
  deleteContactNote,
  fetchContactNotes,
  updateContactNote,
} from '~/redux/actions/contacts-actions';

// Utils
import { dateFormat } from '~/utils/date-format';

// MUI Components
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Skeleton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';

// Icons
import {
  TrashNoteCIcon,
  EditNoteNewCIcon,
  ModalClosePlansCIcon,
  ContactDetailsDeleteCIcon,
} from '~/assets/images';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcButton from '~/components/General/OlcButton';

const Notes = ({ isLoading, contactId }) => {
  const [notes, setNotes] = useState([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [loader, setLoader] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    className: '',
    modalName: '',
  });

  const dispatch = useDispatch();
  const contactNotes = useSelector((state) => state.contactsReducers.contactNotes);

  useEffect(() => {
    if (contactId) {
      dispatch(fetchContactNotes(contactId));
    }
  }, [contactId, dispatch]);

  useEffect(() => {
    if (contactNotes) {
      setNotes(contactNotes);
    }
  }, [contactNotes]);

  const handleAddNote = async () => {
    try {
      setLoader(true);
      if (newNoteContent.trim()) {
        const newNote = {
          contactId: contactId,
          text: newNoteContent.trim(),
        };
        const response = await dispatch(createContactNote(newNote));
        if (response.status === 200) {
          setNotes([...notes, response.data.data]);
          dispatch(success(response.data.message));
        }
      }
      dispatch(fetchContactNotes(contactId));
      setNewNoteContent('');
      setIsAddingNote(false);
    } catch (error) {
      dispatch(failure(error.response.data.message));
    } finally {
      setTimeout(() => {
        setLoader(false);
      }, 1000);
    }
  };

  const handleEditNote = async (noteId, newContent) => {
    try {
      if (!newContent?.trim()) {
        dispatch(failure('Note cannot be empty'));
        return;
      }
      const payload = {
        id: noteId,
        text: newContent,
      };
      const response = await dispatch(updateContactNote(payload));
      if (response.status === 200) {
        dispatch(fetchContactNotes(contactId));
        dispatch(success(response.data.message));
        setEditingNoteId(null);
        setEditNoteContent('');
      }
    } catch (error) {
      dispatch(failure(error.response?.data?.message || 'Failed to update note'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(deleteContactNote({ id: noteId }));
      if (response && (response.status === 200 || response.status === 204)) {
        dispatch(fetchContactNotes(contactId));
        dispatch(success(response.data?.message || 'Note deleted successfully'));
        handleCloseConfirmModal();
      }
    } catch (error) {
      dispatch(failure(error.response?.data?.message || 'Failed to delete note'));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({ ...prev, loading: false }));
      }, 1000);
    }
  };

  const startEditingNote = (noteId, currentContent) => {
    setEditingNoteId(noteId);
    setEditNoteContent(currentContent);
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditNoteContent('');
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal((prev) => ({ ...prev, open: false }));
  };

  const deleteNoteInEditMode = (noteId) => {
    setEditingNoteId(null);
    setEditNoteContent('');
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      message: 'Are you sure you want to delete this note?',
      handleOk: () => handleDeleteNote(noteId),
      successButtonName: 'Yes',
      loading: false,
      header: 'Delete Note',
      handleClose: handleCloseConfirmModal,
      icon: ContactDetailsDeleteCIcon,
      className: 'deleteModal deleteNoteModal',
      modalName: 'deleteNote',
    });
  };

  const handleCancelAddNote = () => {
    setIsAddingNote(false);
    setNewNoteContent('');
  };

  const handleAddNoteAndClose = () => {
    handleAddNote();
    setIsAddingNote(false);
  };

  return (
    <>
      <Card className="notesCard">
        <CardContent>
          <Box className="notesContent" sx={{ position: 'relative' }}>
            {/* Loading Overlay */}
            {loader && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '8px',
                }}
              >
                <CircularProgress size={40} sx={{ color: '#ed5c2f' }} />
              </Box>
            )}

            <Box className="notesHeader">
              <Typography className="notesTitle">Notes</Typography>
              {notes.length !== 0 && (
                <OlcButton
                  variant="primary"
                  className="addNoteButton"
                  onClick={() => setIsAddingNote(true)}
                >
                  Add Note
                </OlcButton>
              )}
            </Box>
            {isLoading ? (
              // Loading skeleton
              <Skeleton
                variant="rectangular"
                width="100%"
                height={120}
                sx={{
                  borderRadius: '8px',
                  marginTop: '16px',
                }}
              />
            ) : notes.length === 0 ? (
              // Empty state
              <Box className="notesEmptyState">
                <TextField
                  placeholder="Add notes about this contact..."
                  multiline
                  rows={4}
                  variant="outlined"
                  className="notesEmptyInput"
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                        {newNoteContent && (
                          <IconButton
                            className="cpClearInputBtn"
                            size="small"
                            onClick={() => setNewNoteContent('')}
                            edge="end"
                            sx={{ right: '15px', top: '25px' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <OlcButton
                  variant="primary"
                  className="NoNoteAddButton"
                  onClick={handleAddNote}
                  disabled={loader || newNoteContent.trim() === ''}
                >
                  Add Note
                </OlcButton>
              </Box>
            ) : (
              // Notes state
              <Box className="notesState">
                {isAddingNote && !editingNoteId ? (
                  // Add note input field
                  <>
                    <Box className="noteEditMode">
                      <TextField
                        placeholder="Add notes about this contact..."
                        multiline
                        rows={2}
                        variant="outlined"
                        className="noteEditInput"
                        value={newNoteContent}
                        onChange={(e) => setNewNoteContent(e.target.value)}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{ width: '16px', marginRight: '8px' }}
                            >
                              {newNoteContent && (
                                <IconButton
                                  className="cpClearInputBtn"
                                  size="small"
                                  onClick={() => setNewNoteContent('')}
                                  edge="end"
                                  sx={{ right: '0px', top: '15px' }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box className="noteEditActions">
                        <OlcButton
                          variant="secondary"
                          className="noteDoneButton"
                          onClick={handleCancelAddNote}
                        >
                          Cancel
                        </OlcButton>
                        <OlcButton
                          variant="primary"
                          icon="save"
                          className="noteDoneButton"
                          onClick={() => handleAddNote()}
                        >
                          Save
                        </OlcButton>
                      </Box>
                    </Box>
                  </>
                ) : null}
                <Box className="notesContainer">
                  {notes.map((note) => (
                    <Box
                      key={note.id}
                      className={`noteItem ${editingNoteId === note.id ? 'editMode' : ''}`}
                    >
                      {editingNoteId === note.id ? (
                        // Edit mode
                        <Box className="noteEditMode">
                          <TextField
                            value={editNoteContent}
                            onChange={(e) => setEditNoteContent(e.target.value)}
                            multiline
                            // rows={3}
                            variant="outlined"
                            className="noteEditInput"
                            fullWidth
                            InputProps={{
                              endAdornment: (
                                <InputAdornment
                                  position="end"
                                  sx={{ width: '16px', marginRight: '8px' }}
                                >
                                  {editNoteContent && (
                                    <IconButton
                                      className="cpClearInputBtn"
                                      size="small"
                                      onClick={() => setEditNoteContent('')}
                                      edge="end"
                                      sx={{ right: '0px' }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              ),
                            }}
                          />
                          <Box className="noteEditActions">
                            <OlcButton
                              variant="secondary"
                              className="noteDoneButton"
                              onClick={cancelEditingNote}
                            >
                              Cancel
                            </OlcButton>
                            <OlcButton
                              variant="primary"
                              icon="save"
                              className="noteDoneButton"
                              onClick={() => handleEditNote(note.id, editNoteContent)}
                            >
                              Save
                            </OlcButton>
                          </Box>
                        </Box>
                      ) : (
                        // View mode
                        <>
                          <Box className="noteHeader">
                            <IconButton
                              className="editNoteButton"
                              size="small"
                              aria-label="Edit note"
                              onClick={() => startEditingNote(note.id, note.text)}
                            >
                              <EditNoteNewCIcon className="editNoteIcon" />
                            </IconButton>
                            <IconButton
                              className="editNoteButton"
                              aria-label="Delete note"
                              onClick={() => deleteNoteInEditMode(note.id)}
                            >
                              <TrashNoteCIcon className="trashNoteIcon" />
                            </IconButton>
                          </Box>
                          <Typography className="noteContent">{note.text}</Typography>
                          <Typography className="noteMetadata">
                            Added on{' '}
                            {dateFormat(
                              note?.updatedAt > note.createdAt ? note.updatedAt : note.createdAt,
                              true
                            )}{' '}
                            by <span className="noteAddedBy">{note?.creator?.fullName}</span>
                          </Typography>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      <ConfirmModal openConfirmModal={openConfirmModal} />
    </>
  );
};

export default Notes;
